import OpenInNewWindow from "@DSG/Icons/OpenInNewWindow";
import { AccordionSectionPresentationProps } from "@DSG/MobiusOverrides/AccordionSection/AccordionSection";
import colorPallete, { bodyFonts, elevation, headlineFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    useGetLink, useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden/Hidden";
import Img from "@insite/mobius/Img/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    topHeadingContent = "topHeadingContent",
    imageFieldsData = "imageFieldsData",
    backgroundColor = "backgroundColor",
    showAdditionalContentOnBottom = "showAdditionalContentOnBottom",
    additionalContentOnBottom = "additionalContentOnBottom",
    buttonFieldsData = "buttonFieldsData",
    showButtonAtBottom = "showButtonAtBottom",
}

interface ImageFieldItem {
    fields: {
        backgroundImage: string;
        showSameBackgroundImageForMobile: boolean;
        imageAltText: string;
        backgroundImageMobile: string;
        showOverlayImage: boolean;
        overlayImage: string;
        imageContent: string; 
        textColor: string;
        textAlignmentHorizontal: "flex-start" | "center" | "flex-end"; 
        textAlignmentVertical: "top" | "middle" | "bottom";
    }
}

const enum variants {
    primaryRed = "primaryRed",
    secondaryRed = "secondaryRed",
    primaryBlack = "primaryBlack",
    secondaryBlack = "secondaryBlack",
}

interface ButtonFieldItem {
    fields: {
        variantType: variants;
        name: string;
        buttonLink: LinkFieldValue;
        openLinkInNewWindow: boolean;
    }
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.topHeadingContent]: string;
        [fields.imageFieldsData]: ImageFieldItem[];
        [fields.backgroundColor]: string;
        [fields.showAdditionalContentOnBottom]: boolean;
        [fields.additionalContentOnBottom]: string;
        [fields.buttonFieldsData]: ButtonFieldItem[];
        [fields.showButtonAtBottom]: boolean;
    };
}

export interface DsgFindingMyTradeDonorStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    headingWrapper?: InjectableCss;
    cardWrapper?: InjectableCss;
    card?: InjectableCss;
    cardTitle?: TypographyProps;
    cardLink?: LinkPresentationProps;
    imageWrapper?: InjectableCss;
    bottomWrapper?: InjectableCss;
    bottomContentWrapper?: InjectableCss;
    imageOverlayWrapper?: InjectableCss;
    overlayContentWrapper?: InjectableCss;
    buttonBottomWrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
    button?: InjectableCss;
}

export const dsgFindingMyTradeDonorStyles: DsgFindingMyTradeDonorStyles = {
    wrapper: {
        css: css`
            margin: 24px 0;
            padding: 40px 16px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin: 24px -24px;
                        padding: 24px 0;
                    `,
                ],
                "min",
            )}
        `
    },
    innerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    margin: 0 auto;
                    gap: 24px;
                `,
                ],"min"
            )}
        `
    },
    headingWrapper: {
        css: css`
            max-width: 1152px;
            margin: 0 auto;
            > div,
            & {
                display: flex;
                gap: 20px; 
                flex-direction: column;
                text-align: center;
                > [class*="TypographyStyle"] {
                    margin-bottom: 0;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    padding: 0 16px;
                    > div,
                    & {
                        gap: 24px; 
                    }
                `,
                ],"min"
            )}
        `
    },
    cardWrapper: {
        css: css`
            display: flex;
            gap: 24px 0;
            flex-wrap: wrap;
            justify-content: center;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    gap: 24px 16px;
                `,
                ],"min"
            )}
        `
    },
    card: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            position: relative;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    width: calc(100%/3 - 11px);
                `,
                css`
                `,
                ],"min"
            )}
        `
    },
    imageWrapper: {
        css: css`
            margin-bottom: auto;
            display: flex;
            align-items: center;
            &,
            > div {
                height: 100%;
                width: 100%;
            }
            img {
                width: 100%;
                height: 100%;
                border-radius: 8px;
            }
        `  
    },
    imageOverlayWrapper: {
        css: css`
            position: absolute;
            top: 0;
            width: 100%;
            img {
                width: 100%;
                height: 100%;
                border-radius: 8px;
            }
        `
    },
    overlayContentWrapper: {
        css: css`
            position: absolute;
            top: 0;
            height: 100%;
            width: 100%;
            display: flex;
            padding: 35px 24px;
            font: ${headlineFonts.headline3SemiBoldLg.fontWeight} ${headlineFonts.headline3SemiBoldLg.fontSize}/${headlineFonts.headline3SemiBoldLg.lineHeight} ${headlineFonts.headline3SemiBoldLg.fontFamily};
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    padding: 20px 16px;
                `,
                css`
                    padding: 50px 29px;
                `,
                ],"min"
            )}
        `
    },
    bottomContentWrapper: {
        css: css`
            margin-top: 4px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    margin-top: 0;
                `,
                ],"min"
            )}
        `
    },
    bottomWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 24px;
            justify-content: center;
            max-width: 1152px;
            margin: auto;
            text-align: center;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    gap: 16px;
                    padding: 0 16px;
                `,
                ],"min"
            )}
        `
    },
    buttonBottomWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 24px 40px;
            justify-content: center;
        `
    },
    buttonWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    &,
                    > a {
                        width: 100%;
                    }
                `,
                ],"max"
            )}
        `
    }
};

const styles = dsgFindingMyTradeDonorStyles;

export const DsgFindingMyTradeDonor: FC<OwnProps> = ({ fields }) => {

    const { imageFieldsData } = fields;

    const linkData = fields.buttonFieldsData.filter(
        (item) => item && item.fields && item.fields.buttonLink
    );
    
    const links = useGetLinks(linkData, (item) => item?.fields?.buttonLink);
    
    return (
        <StyledWrapper {...styles.wrapper}
            className="donorComponentWrapper"  
            style={{backgroundColor: fields.backgroundColor ? `${fields.backgroundColor}` : '#F6F6F7'}}>
            <StyledWrapper {...styles.innerWrapper}>
                {fields.topHeadingContent && (
                    <StyledWrapper {...styles.headingWrapper}>
                        {parse(fields.topHeadingContent, parserOptions)}
                    </StyledWrapper>
                )}
                {imageFieldsData.length > 0 && (
                    <StyledWrapper {...styles.cardWrapper}>
                        {imageFieldsData.map((item, index) => {
                            return (
                                <StyledWrapper {...styles.card} key={index}>
                                    {item.fields.backgroundImage && item.fields.showSameBackgroundImageForMobile && (
                                        <StyledWrapper {...styles.imageWrapper}>
                                            <Img src={item.fields.backgroundImage} altText={item.fields.imageAltText || ''}/>
                                        </StyledWrapper>
                                    )}
                                    {item.fields.backgroundImage && item.fields.backgroundImageMobile && !item.fields.showSameBackgroundImageForMobile && (
                                        <StyledWrapper {...styles.imageWrapper}>
                                            <Hidden below="md">
                                                <Img src={item.fields.backgroundImage} altText={item.fields.imageAltText || ''}/>
                                            </Hidden>
                                            <Hidden above="sm">
                                                <Img src={item.fields.backgroundImageMobile} altText={item.fields.imageAltText || ''} />
                                            </Hidden>
                                        </StyledWrapper>
                                    )}
                                    {(item.fields.overlayImage && item.fields.showOverlayImage) && (
                                        <StyledWrapper {...styles.imageOverlayWrapper}>
                                            <Img src={item.fields.overlayImage} altText={translate('Visual overlay with static text displayed on it.')} />
                                        </StyledWrapper>
                                    )}
                                    {item.fields.imageContent && (
                                        <StyledWrapper {...styles.overlayContentWrapper} 
                                            style={{ 
                                                alignItems: item.fields.textAlignmentVertical,
                                                justifyContent: item.fields.textAlignmentHorizontal,
                                                textAlign: (item.fields.textAlignmentHorizontal === 'flex-start' ? 'left' :
                                                item.fields.textAlignmentHorizontal === 'flex-end' ? 'right' :
                                                item.fields.textAlignmentHorizontal === 'center' ? 'center' :
                                                'left'),
                                                color: item.fields.textColor,
                                            }} 
                                        >
                                            {parse(item.fields.imageContent, parserOptions)}
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                            )
                        })}
                    </StyledWrapper>
                )}
               
                {(fields.showButtonAtBottom && fields.buttonFieldsData.length > 0) && (
                    <StyledWrapper {...styles.bottomWrapper}>
                         {fields.additionalContentOnBottom && (
                            <StyledWrapper {...styles.bottomContentWrapper}>
                                {parse(fields.additionalContentOnBottom, parserOptions)}
                            </StyledWrapper>
                        )}
                        
                        <StyledWrapper {...styles.buttonBottomWrapper}>
                            {fields.buttonFieldsData.map((item, index) => {
                                const link = links[index];
                                if (!link || !link.url) {
                                    return null; 
                                }
                                return (
                                    <StyledWrapper {...styles.buttonWrapper} key={index}>
                                        {(item.fields.name ) && (
                                            <Link href={link.url || '#'}
                                                target={item.fields.openLinkInNewWindow ? '_blank': ''} 
                                                {...styles.button}   
                                                className= {`dsgCMSBtn ${item.fields.variantType ? '' : 'withoutNewWindowIcon'} ${
                                                    item.fields.variantType === variants.primaryRed ? 'dsgPrimaryRed' :
                                                    item.fields.variantType === variants.secondaryRed ? 'dsgSecondaryRed' :
                                                    item.fields.variantType === variants.primaryBlack ? 'dsgPrimaryBlack' :
                                                    item.fields.variantType === variants.secondaryBlack ? 'dsgSecondaryBlack' : ''
                                                }`}
                                                >
                                                {item.fields.name} 
                                                {item.fields.openLinkInNewWindow && (
                                                    <OpenInNewWindow/>
                                                )}
                                            </Link>
                                        )}
                                    </StyledWrapper>
                                )
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};



const widgetModule: WidgetModule = {
    component: DsgFindingMyTradeDonor,
    definition: {
        group: "Basic",
        displayName: "Finding My Trade - Donor",
        fieldDefinitions: [
            {
                name: fields.topHeadingContent,
                editorTemplate: "RichTextField",
                displayName: "Top Heading Section",
                defaultValue: "",
                fieldType: "General",
                extendedConfig: { height: 170 },
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "#0D3954",
            },
            {
                name: fields.imageFieldsData,
                editorTemplate: "ListField",
                displayName: "Images Data",
                getDisplay: (item: HasFields) => {
                    const {
                        imageContent,
                    } = item.fields;
                    if (imageContent) {
                        return imageContent || "Image";
                    }
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "imageContent",
                        editorTemplate: "TextField",
                        displayName: "Image Text",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "backgroundImage",
                        displayName: 'Background Image',
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "imageAltText",
                        editorTemplate: "TextField",
                        displayName: "Image Alt Text",
                        defaultValue: "",
                    },
                    {
                        name: "showSameBackgroundImageForMobile",
                        displayName: "Show Same Background Image For Mobile",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "backgroundImageMobile",
                        displayName: 'Background Image For Mobile',
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => !widget.fields.showSameBackgroundImageForMobile,
                    },
                    {
                        name: "showOverlayImage",
                        displayName: "Show Overlay Image",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                    },
                    {
                        name: "overlayImage",
                        displayName: 'Overlay Image',
                        editorTemplate: "ImagePickerField",
                        defaultValue: "/UserFiles/GradientOverlay.png",
                        isVisible: widget => widget.fields.showOverlayImage,
                    },
                    {
                        name: "textColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Text Color",
                        defaultValue: "#ffffff",
                    },
                    {
                        name: "textAlignmentHorizontal",
                        displayName: 'Text Alignment Horizontal',
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "flex-start",
                        options: [
                            {
                                displayName: "Left",
                                value: "flex-start",
                            },
                            {
                                displayName: "Center",
                                value: "center",
                            },
                            {
                                displayName: "Right",
                                value: "flex-end",
                            },
                        ],
                    },
                    {
                        name: "textAlignmentVertical",
                        displayName: 'Text Alignment Vertical',
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "flex-start",
                        options: [
                            {
                                displayName: "Top",
                                value: "flex-start",
                            },
                            {
                                displayName: "Middle",
                                value: "center",
                            },
                            {
                                displayName: "Bottom",
                                value: "flex-end",
                            },
                        ],
                    },
                ],
            },
            {
                name: fields.showAdditionalContentOnBottom,
                displayName: "Show additional content on bottom",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.additionalContentOnBottom,
                editorTemplate: "RichTextField",
                displayName: "Additional Content On Bottom",
                defaultValue: "",
                extendedConfig: { height: 170 },
                fieldType: "General",
                isVisible: widget => widget.fields.showAdditionalContentOnBottom,
            },
            {
                name: fields.showButtonAtBottom,
                displayName: "Show button on bottom",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.buttonFieldsData,
                editorTemplate: "ListField",
                displayName: "Buttons Data",
                getDisplay: (item: HasFields) => {
                    const {
                        name,
                    } = item.fields;
                    if (name) {
                        return name || "Button";
                    }
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        displayName: "Name",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "variantType",
                        displayName: "Variant Type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary Red", value: variants.primaryRed },
                            { displayName: "Secondary Red", value: variants.secondaryRed },
                            { displayName: "Primary Black", value: variants.primaryBlack },
                            { displayName: "Secondary Black", value: variants.secondaryBlack },
                        ],
                        defaultValue: variants.primaryRed,
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link Url",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "openLinkInNewWindow",
                        displayName: "Open Link In New Window",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                isVisible: widget => widget.fields.showButtonAtBottom,
            },
        ],
    },
};

export default widgetModule;
